import React from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '../ui/Input/Input';
import { Button } from '../ui/Button/Button';
import { Alert } from '../ui/Alert/Alert';
import { apiFetch } from '@/lib/api';

type PageState = 'verifying' | 'invalid' | 'form' | 'success';

const PASSWORD_CHECKS = [
  { label: 'At least 12 characters', test: (p: string) => p.length >= 12 },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
  { label: 'One symbol', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [state, setState] = React.useState<PageState>('verifying');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Verify token on mount
  React.useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    (async () => {
      try {
        const res = await apiFetch('/api/auth/verify-reset-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
          skipRefresh: true,
        });
        setState(res.ok ? 'form' : 'invalid');
      } catch {
        setState('invalid');
      }
    })();
  }, [token]);

  const allChecksPassed = PASSWORD_CHECKS.every(c => c.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allChecksPassed || !passwordsMatch) return;
    setError(null);
    setIsLoading(true);
    try {
      const res = await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
        skipRefresh: true,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error?.message || 'Failed to reset password');
      }
      setState('success');
      setTimeout(() => navigate('/auth', { replace: true }), 3000);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (state) {
      case 'verifying':
        return (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Verifying your reset link...</p>
          </div>
        );

      case 'invalid':
        return (
          <div className="text-center">
            <div className="w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Link expired or invalid</h2>
            <p className="text-sm text-muted-foreground mb-6">
              This password reset link has expired or has already been used. Please request a new one.
            </p>
            <Link to="/forgot-password">
              <Button variant="primary">Request New Link</Button>
            </Link>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-14 h-14 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Password reset successful</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Your password has been updated. Redirecting to sign in...
            </p>
            <Link
              to="/auth"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Go to sign in now
            </Link>
          </div>
        );

      case 'form':
        return (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-foreground">Set new password</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Choose a strong password for your account
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              <Input
                label="New Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••••"
                required
              />

              {/* Password strength checklist */}
              {password.length > 0 && (
                <div className="space-y-1.5">
                  {PASSWORD_CHECKS.map((check, i) => {
                    const passed = check.test(password);
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={passed ? 'text-success' : 'text-muted-foreground'}>
                          {passed ? '✓' : '○'}
                        </span>
                        <span className={passed ? 'text-success' : 'text-muted-foreground'}>
                          {check.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <Input
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••••••"
                required
              />

              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="text-xs text-destructive">Passwords do not match</p>
              )}

              {error && (
                <Alert variant="error">{error}</Alert>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={isLoading}
                disabled={!allChecksPassed || !passwordsMatch}
              >
                Reset Password
              </Button>

              <div className="text-center">
                <Link
                  to="/auth"
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/auth" className="inline-block hover:opacity-80 transition-opacity">
            <img
              src="/images/logo_moniker_cockpit.png"
              alt="Moniker Cockpit"
              className="h-8 mx-auto mb-4 object-contain"
            />
            <p className="text-sm text-muted-foreground mt-1">Company Automation Platform</p>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-8">
            {renderContent()}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </div>
      </div>
    </div>
  );
}
